/**
 * PM token balance store — single source of truth for the in-chat
 * low-balance gate (`ChatPanel.handleSendMessage`) and any other surface
 * that needs to know "how many tokens does this user have right now."
 *
 * Initial value is `null` (unknown) until either:
 *   - `lobby:balance-update` WS frame arrives on lobby connect (mirrors
 *     mobile `pmBalanceProvider`); or
 *   - A caller (e.g. `DashboardPage`, post-IAP success poll) explicitly
 *     hydrates via `setPmBalance(...)` after a REST fetch.
 *
 * The chat gate treats `null` as "don't gate" so the bubble never flashes
 * during the cold-start REST round-trip — mirrors the
 * `_shouldGateLowBalance` semantics in mobile's `lobby_providers.dart`.
 *
 * Mirrors mobile `pmBalanceProvider` (`apps/mobile/lib/state/billing_providers.dart`).
 */
import { create } from 'zustand';

let nextFetchSeq = 0;

/**
 * Call once when STARTING a balance fetch, before the awaited request
 * resolves; pass the returned token to `setPmBalance` as `seq`. Two
 * concurrent fetches (e.g. a page's mount fetch racing a post-reward
 * refresh) can resolve out of order — without a sequence guard, the
 * slower/older one would silently overwrite the newer value with a stale
 * one (night-watch 2026-07-11). Callers that don't pass `seq` keep the
 * previous unguarded last-write-wins behavior.
 */
export function nextPmBalanceFetchSeq(): number {
  nextFetchSeq += 1;
  return nextFetchSeq;
}

interface PmBalanceState {
  pmBalance: number | null;
  lastAppliedSeq: number;
  setPmBalance: (value: number, seq?: number) => void;
  reset: () => void;
}

export const usePmBalanceStore = create<PmBalanceState>()((set, get) => ({
  pmBalance: null,
  lastAppliedSeq: 0,
  setPmBalance: (value, seq) => {
    if (seq !== undefined && seq < get().lastAppliedSeq) {
      return;
    }
    set({ pmBalance: value, lastAppliedSeq: seq ?? get().lastAppliedSeq });
  },
  reset: () => set({ pmBalance: null, lastAppliedSeq: 0 }),
}));
